import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Bug, X, Send, MessageSquare } from 'lucide-react';
import { trackEvent, getPostHog } from '../lib/posthog';
import { POSTHOG_HOST } from '../config';
import FeedbackForm from './FeedbackForm';

const BugReport = ({ isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [bugText, setBugText] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThanks, setShowThanks] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const getSessionRecordingUrl = () => {
    const posthog = getPostHog();
    if (!posthog) return undefined;

    try {
      const sessionId = posthog?.sessionRecording?.sessionId;
      if (!sessionId) return undefined;

      // Look back 30 seconds before the bug report
      const LOOK_BACK = 30;
      const sessionStartTime = posthog?.sessionManager?._sessionStartTimestamp || 0;
      const recordingStartTime = Math.max(
        Math.floor((new Date().getTime() - sessionStartTime) / 1000) - LOOK_BACK,
        0
      );

      // Use PostHog's config API host as primary, fallback to POSTHOG_HOST, then default
      const apiHost = posthog?.config?.api_host || POSTHOG_HOST || 'https://us.i.posthog.com';
      return `${apiHost}/recordings/${sessionId}?t=${recordingStartTime}`;
    } catch (error) {
      console.error('Error generating session recording URL:', error);
      return undefined;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bugText.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const posthog = getPostHog();
      const sessionRecordingUrl = getSessionRecordingUrl();
      const sessionId = posthog?.sessionRecording?.sessionId || undefined;

      // Capture the bug report event
      trackEvent('bug Sent', {
        $bug: bugText.trim(),
        email: email.trim() || undefined,
        sessionRecordingUrl: sessionRecordingUrl,
        $session_id: sessionId,
      });

      // Show success message
      setShowThanks(true);
      setBugText('');
      setEmail('');

      // Close modal after 3 seconds
      setTimeout(() => {
        setShowThanks(false);
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting bug report:', error);
      alert('Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setBugText('');
    setEmail('');
    setShowThanks(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && menuRef.current && buttonRef.current && 
          !menuRef.current.contains(event.target) && 
          !buttonRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const handleFeedbackClick = () => {
    setShowMenu(false);
    setShowFeedbackForm(true);
  };

  const handleBugReportClick = () => {
    setShowMenu(false);
    setIsOpen(true);
  };

  // Listen for custom events from Footer
  useEffect(() => {
    const handleOpenBugReport = () => {
      setIsOpen(true);
    };

    const handleOpenFeedbackForm = () => {
      setShowFeedbackForm(true);
    };

    window.addEventListener('openBugReport', handleOpenBugReport);
    window.addEventListener('openFeedbackForm', handleOpenFeedbackForm);

    return () => {
      window.removeEventListener('openBugReport', handleOpenBugReport);
      window.removeEventListener('openFeedbackForm', handleOpenFeedbackForm);
    };
  }, []);

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-50" ref={buttonRef}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
            isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          } hover:scale-110 hover:shadow-xl`}
          aria-label="Feedback & Bug Report"
          title="Feedback & Bug Report"
        >
          <MessageCircle className="w-6 h-6" />
        </button>

        {/* Menu Dropdown */}
        {showMenu && (
          <div
            ref={menuRef}
            role="menu"
            className={`absolute bottom-full right-0 mb-2 w-48 rounded-lg shadow-lg border z-50 ${
              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <button
              role="menuitem"
              className={`feedback-button w-full text-left px-4 py-3 text-sm rounded-t-lg flex items-center gap-3 transition-colors ${
                isDarkMode
                  ? 'text-gray-200 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={handleFeedbackClick}
            >
              <MessageSquare className="w-4 h-4" />
              Give Feedback
            </button>
            <button
              role="menuitem"
              className={`w-full text-left px-4 py-3 text-sm rounded-b-lg flex items-center gap-3 transition-colors ${
                isDarkMode
                  ? 'text-gray-200 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={handleBugReportClick}
            >
              <Bug className="w-4 h-4" />
              Report a Bug
            </button>
          </div>
        )}
      </div>

      {/* Modal Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm"
          onClick={handleCancel}
        >
          <div
            className={`relative max-w-md w-full rounded-lg shadow-xl ${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCancel}
              className={`absolute top-4 right-4 p-1 rounded-md transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            {showThanks ? (
              <div className="p-6 text-center">
                <div className={`text-lg font-semibold mb-2 ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  Thank you!
                </div>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Your bug report has been submitted. Closing in 3 seconds...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6">
                <h2 className={`text-xl font-semibold mb-4 ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  Report a Bug
                </h2>

                {/* Bug Description */}
                <div className="mb-4">
                  <label
                    htmlFor="bug-text"
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    What went wrong?
                  </label>
                  <textarea
                    id="bug-text"
                    value={bugText}
                    onChange={(e) => setBugText(e.target.value)}
                    placeholder="Describe the bug you encountered..."
                    required
                    rows={5}
                    className={`w-full px-4 py-3 rounded-lg border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>

                {/* Email (Optional) */}
                <div className="mb-6">
                  <label
                    htmlFor="bug-email"
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Email (optional)
                  </label>
                  <input
                    id="bug-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                  <p className={`text-xs mt-1 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    We'll use this to follow up if needed
                  </p>
                </div>

                {/* Footer */}
                <div className="border-t pt-4 flex justify-between items-center">
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    Have a specific issue?{' '}
                    <a
                      href="mailto:founders@civicduty.app"
                      className="text-blue-600 hover:underline"
                    >
                      Contact support directly
                    </a>
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      isDarkMode
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!bugText.trim() || isSubmitting}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      !bugText.trim() || isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    {isSubmitting ? 'Sending...' : 'Send Bug'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Feedback Form Modal */}
      <FeedbackForm
        isDarkMode={isDarkMode}
        isOpen={showFeedbackForm}
        onClose={() => setShowFeedbackForm(false)}
      />
    </>
  );
};

export default BugReport;
